
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Profile {
  id: string;
  user_id: string;
  username: string | null;
  farm_name: string | null;
  bio: string | null;
  location: string | null;
  usda_zone: string | null;
  hardiness_zone_id: string | null;
  avatar_url: string | null;
  farm_logo_url: string | null;
  farm_website: string | null;
  farming_since: string | null;
  farm_size: string | null;
  farm_images: any;
  farm_goals: string[] | null;
  latitude: number | null;
  longitude: number | null;
  is_discoverable: boolean;
  location_visible: boolean;
  profile_tags: string[] | null;
  is_active: boolean;
}

export const useProfile = () => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current authenticated session first to avoid AuthSessionMissingError
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        // Non-fatal in anonymous mode – just treat as no session
        console.warn('Auth session check error (continuing anonymously):', sessionError.message);
      }

      const user = session?.user || null;

      if (!user) {
        // No authenticated user – anonymous browsing supported
        setProfile(null);
        return;
      }

      // Fetch the current user's profile
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') {
        // PGRST116 is "no rows found" which is expected for new users
        throw profileError;
      }

      if (userProfile) {
        console.log('User profile found:', userProfile);
        setProfile(userProfile);
      } else {
        console.log('No profile found for user, will need to create one');
        setProfile(null);
      }
    } catch (err) {
      console.error('Error in fetchProfile:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const refetch = () => {
    fetchProfile();
  };

  useEffect(() => {
    fetchProfile();

    // Listen for auth changes to refetch profile when user changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        fetchProfile();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return {
    profile,
    loading,
    error,
    refetch,
  };
};
